import {Component, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {AutoComplete, AutoCompleteCompleteEvent} from 'primeng/autocomplete';
import {TranslatePipe} from '@ngx-translate/core';
import {ReactiveFormsModule} from '@angular/forms';
import {FormLabelComponent, JPATableLazyEvent, UtilsService} from '@shared-lib';
import {map} from 'rxjs';
import {UserInfoModel} from '../../models';
import {UtentiService} from '../../../core/service';

@Component({
	selector: 'form-utenti-autocomplete',
	imports: [AutoComplete, TranslatePipe, ReactiveFormsModule, FormLabelComponent],
	template: `
    <div class="flex flex-column">
      @if (label) {
        <form-label [label]="label"
                    [infoTooltipText]="infoTooltipText"
                    [forceLabelDanger]="forceLabelDanger">
        </form-label>
      }
      <p-autoComplete #autocompleteEmailRef autocomplete="new-email"
                      [formControl]="control"
                      [suggestions]="filteredUsers"
                      [dropdown]="showDropdown"
                      [forceSelection]="false"
                      [showClear]="!readonly"
                      [minQueryLength]="4"
                      [emptyMessage]="'primeng.emptyMessage'|translate"
                      [appendTo]="'body'"
                      [readonly]="readonly"
                      (completeMethod)="searchUserAutocomplete($event)"
                      (onDropdownClick)="searchUserAutocomplete()"
                      (onSelect)="userSelection($event.value)"
                      (onClear)="selectedUserData.emit(null)">
        <ng-template let-user #item>
          <div class="flex gap-2">
            <div class="font-bold">{{ !user.displayName.includes('null') ? user.displayName : ' N/A ' }}</div>
            •
            <div class="text-sm text-gray-600">{{ user.uid }}</div>
            @if (user.azienda?.nome?.length > 0) {
              •
              <div class="text-sm text-gray-600">{{ user.azienda.nome }}</div>
            }
          </div>
        </ng-template>
      </p-autoComplete>
    </div>
	`
})
export class FormUtentiAutocompleteComponent {
	@ViewChild('autocompleteEmailRef') autoComplete: AutoComplete;

	@Input() label: string;
	@Input() infoTooltipText: string;
	@Input() forceLabelDanger = false;
	@Input() aziendaDossierId: number;
	@Input() forceAziendaFilter = false; // forza filtro per azienda (per es. su contatti dossier)

	@Input() control: any;
	@Input() readonly: boolean = false;
	@Input() exclusions: string[] = []; // email da escludere dall'autocomplete

	@Output() onValidMailWithNoResults = new EventEmitter<void>();
	@Output() selectedUserData = new EventEmitter<UserInfoModel>();

	filteredUsers: UserInfoModel[] = [];
	lastResults: UserInfoModel[] = [];
	selectedUser: UserInfoModel;
	lastQuery: string = '';
	showDropdown = false;

	private emptyResultsTimer: any;

	constructor(private utentiService: UtentiService,
	            protected utils: UtilsService) {
	}

	searchUserAutocomplete(event?: AutoCompleteCompleteEvent) {
		const query = event?.query?.trim();
		if (!query) {
			// dropdown click → mostra ultimi risultati
			this.filteredUsers = [...this.lastResults];
			return;
		} else if (query.length < 4) {
			return;
		}
		this.showDropdown = !!(query?.length >= 4 || this.lastResults.length > 0);
		this.lastQuery = query;
		if(this.forceAziendaFilter) {
			const queryRequest: JPATableLazyEvent = {filter: {'uid.contains': query, 'aziendaId.equals': this.aziendaDossierId}};
			this.utentiService.getAccessibili(queryRequest).pipe(map(resp => resp.body)).subscribe(users => {
				this.filteredUsers = users.filter(u => !this.exclusions.includes(u.uid));
				this.lastResults = users;
				// mostro per mandare invito se email è valida
				if (users.length === 0 && this.control.valid) {
					this.onValidMailWithNoResults.emit();
				}
				// se NON ci sono risultati --> avvia timer chiusura
				if (users.length === 0) {
					this.startAutoCloseTimer();
				} else {
					this.clearAutoCloseTimer();
				}
			});		
		}else{
		// TODO: filtare a dominio per UO, ecc. SE sono su pag. Contatti Dossier (vedi analisi)
			const queryRequest: JPATableLazyEvent = {filter: {'uid.contains': query}};
			this.utentiService.getAllV2(queryRequest).pipe(map(resp => resp.body)).subscribe(users => {
				this.filteredUsers = users.filter(u => !this.exclusions.includes(u.uid));
				this.lastResults = users;
				// mostro per mandare invito se email è valida
				if (users.length === 0 && this.control.valid) {
					this.onValidMailWithNoResults.emit();
				}
				// se NON ci sono risultati --> avvia timer chiusura
				if (users.length === 0) {
					this.startAutoCloseTimer();
				} else {
					this.clearAutoCloseTimer();
				}
			});
		}
	}

	userSelection(userSelected: UserInfoModel) {
		this.selectedUser = userSelected;
		this.control.setValue(userSelected.uid);
		this.selectedUserData.emit(userSelected); // dati completi utente per gestione esterna...
	}

	private startAutoCloseTimer() {
		this.clearAutoCloseTimer();
		this.emptyResultsTimer = setTimeout(() => this.closeAutoCompletePanel(), 3000);
	}

	private clearAutoCloseTimer() {
		if (this.emptyResultsTimer) {
			clearTimeout(this.emptyResultsTimer);
			this.emptyResultsTimer = null;
		}
	}

	private closeAutoCompletePanel() {
		this.autoComplete?.hide();
	}

}
